import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { HRMetric } from 'dataVisualizations/heartRateLineGraph/hrLineGraphTypes';
import { Serie } from '@nivo/line';
import { BarData, LineData } from 'dataVisualizations/types/types';
import { Colors } from '@whoop/web-components';
import {
  MetricOverview, Metrics,
} from 'types/analytics';
import { formatDate } from 'progress/profile/profileUtils';

dayjs.extend(duration);

// Turns seconds into HH:MM format
export const formatDurationFromSeconds = (seconds: number) => dayjs.duration(seconds, 'seconds').format('HH:mm');

export const getHourFromEpoch = (epochTime: number) => {
  const hourWithAmPm = dayjs(epochTime).format('hh:mm A');
  const [hour, amPm] = hourWithAmPm.split(' ');
  return {
    formattedTime: hourWithAmPm,
    hour,
    amPm,
  };
};

export const formatData = (metrics: HRMetric[]): Serie => {
  const dataInFormat: Serie = {
    id: 'hr_data',
    data: [],
  };
  dataInFormat.data = metrics.map((d: any) => ({
    x: d.timestamp,
    y: d.hr,
  }));
  return dataInFormat;
};

// This handles a common range type sent from the backend
// Example "[2021-01-01T00:00:00.000Z, 2021-01-01T08:00:00.000Z)"
export const getDuration = (range: string) => {
  const rangeArr = range.split('\'');
  const lowerBound = dayjs(rangeArr[1]).valueOf();
  const upperBound = dayjs(rangeArr[3]).valueOf();
  const activityDuration = dayjs.duration(upperBound - lowerBound).format('H:mm');
  return activityDuration;
};

export const createDayBarLineGraphData = (
  data: MetricOverview,
  formatterParam: (val: number) => number | null,
  addToDataObject?: (metric: Metrics) => {},
): BarData[] | LineData[] => {
  if (!data) {
    return null;
  }
  const formatter = formatterParam ?? Math.round;
  let parsedData: BarData[] | LineData[];

  if (data.metrics.length <= 7) {
    parsedData = data.metrics.map((metric: Metrics) => ({
      // @ts-ignore metric.cycle_start_day is of format [2022, 11, 15] which works fine for dayjs
      cycle_start_day: formatDate(metric.cycle_start_day.join('-')),
      metric_value: formatter(metric.metric_value),
      ...metric.metric_details,
      ...(addToDataObject && addToDataObject(metric)),
    }));
  } else {
    parsedData = data.metrics.map((metric) => ({
      // @ts-ignore metric.cycle_start_day is of format [2022, 11, 15] which works fine for dayjs
      x: formatDate(metric.cycle_start_day.join('-')),
      y: formatter(metric.metric_value),
    }));
  }
  return parsedData;
};

export const getApproxMinFromVals = (minVal: number, maxVal: number) => {
  const millisecondDifference = maxVal - minVal;
  return Math.round((millisecondDifference / 1000) / 60);
};

export const getTimeStepFromMin = (approxMin: number) => {
  if (approxMin > 120) {
    return 60;
  }
  if (approxMin > 60) {
    return 30;
  }
  if (approxMin > 30) {
    return 20;
  }
  if (approxMin > 20) {
    return 10;
  }
  if (approxMin > 5) {
    return 5;
  }
  return approxMin;
};

const RECOVERY_RED_MAX = 33;
const RECOVERY_YELLOW_MAX = 66;

export const getColorFromRecovery = (value: number, hidden: boolean = false): string => {
  if (hidden) {
    return Colors.gray500;
  }
  if (value <= RECOVERY_RED_MAX) {
    return Colors.red;
  } if (value <= RECOVERY_YELLOW_MAX) {
    return Colors.yellow;
  }
  return Colors.green;
};
